import { required, useBreadcrumb, useStore, useVuelidate } from '@/plugins'
import { computed, ref } from 'vue'
import { navigateToItem, notifyValidation } from '@/utils/helpers.js'
import { setSnackbar } from 'best-modules/plugins'
import { fillDirectory } from '@/directory'
import { TaskCredentials } from '@/utils/types/task'
import { cachedObject } from 'best-modules/utils'
import { openDialog } from '@/dialog'
import { cloneDeep } from 'lodash'
import { taskCreate } from '@/requests/task'

export function useCreateTask(taskData: any) {
  const store = useStore()
  const breadcrumb = useBreadcrumb()
  const submitLoading = ref(false)
  const typeId = taskData?.typeId || 1
  const entityId = taskData?.entityId
  const userId = computed(() => store.state.user.userData?.id)

  fillDirectory(['taskTypes'])

  const taskType = {
    execution: typeId === 1,
    errand: [3, 4].includes(typeId),
    familiarization: false,
  }

  if (taskData) {
    breadcrumb.set([
      {
        title: taskData.name,
        to: {
          name: [3, 4].includes(taskData?.entityTypeId)
            ? 'single-order'
            : 'correspondence-single',
          params: { id: taskData.id },
        },
        index: 1,
      },
      {
        title: taskType.errand ? 'Доручення' : 'Ознайомлення',
        index: 2,
      },
    ])
  }

  const getDefaultTopic = () => {
    if (!taskData || taskType.execution) return null
    const topicTitle = taskType.errand
      ? 'Доручення'
      : 'Ознайомлення з документом'
    const docName = taskData ? `: "${taskData.name ? taskData.name : ' '}"` : ''
    return `${topicTitle}${docName}`
  }
  const getDefaultDescription = () => {
    return taskType.familiarization ? 'Прошу ознайомитись з документом' : null
  }
  const getDefaultAttachment = () => {
    return taskData ? taskData.document : []
  }
  const getDefaultAuthorId = () => {
    return taskData?.issuedUserId ? taskData.issuedUserId : userId.value
  }
  const getDefaultDeadline = () => {
    return taskData ? taskData.familiarizationTerm || null : null
  }
  const getDefaultContractor = () => {
    return taskData ? taskData.contractorId : null
  }

  const task = cachedObject<TaskCredentials>({
    authorId: getDefaultAuthorId(),
    typeId: typeId,
    topic: getDefaultTopic(),
    description: getDefaultDescription(),
    isHighImportance: false,
    executorId: null,
    deadline: getDefaultDeadline(),
    attachment: getDefaultAttachment(),
    contractorId: getDefaultContractor(),
    entityId: entityId,
    entityTypeId: taskData?.entityTypeId || null,
    observers: [],
    controlUsers: [],
    isCEO: false,
    executor: null,
  } as TaskCredentials)

  const rules = computed(() => {
    return {
      authorId: !taskType.execution ? { required } : {},
      topic: taskType.execution ? { required } : {},
      description: taskType.errand ? {} : { required },
      executorId: !taskType.familiarization ? { required } : {},
      deadline: { required },
      attachment: {},
    }
  })
  const v$ = useVuelidate(rules, task)

  const submit = () => {
    v$.value.$validate()
    if (!v$.value.$error) {
      submitLoading.value = true
      const obj = cloneDeep(task.$object)
      obj.observers = task.observers.map(o => o.id)
      obj.controlUsers = task.controlUsers.map(o => o.id)
      return taskCreate(obj).then(res => {
        submitLoading.value = false
        const taskTypeName = taskType.errand ? 'Доручення' : 'Завдання'
        setSnackbar({
          text: `${taskTypeName} успішно створено`,
        })
        navigateToItem('single-task', res.id)
      })
    } else {
      notifyValidation(v$.value)
    }
  }
  const cancel = () => {
    navigateToItem('tasks')
  }

  const executorDialog = () => {
    openDialog({
      name: 'SelectDepartmentPerson',
      dialogData: {
        userIds: [task.executorId],
        isSingle: true,
      },
      params: {
        cb: ({ users }) => {
          task.executor = users[0]
          task.executorId = users[0].id
        },
      },
    })
  }

  const observerUsersDialog = () => {
    openDialog({
      name: 'SelectDepartmentPerson',
      dialogData: {
        userIds: task.observers.map(o => o.id),
        canBeEmpty: true,
      },
      params: {
        cb: ({ users }) => {
          task.observers = users
        },
      },
    })
  }

  const controlUsersDialog = () => {
    openDialog({
      name: 'SelectDepartmentPerson',
      dialogData: {
        userIds: task.controlUsers.map(o => o.id),
        canBeEmpty: true,
      },
      params: {
        cb: ({ users }) => {
          task.controlUsers = users
        },
      },
    })
  }

  return {
    submitLoading,
    taskType,
    typeId,
    task,
    v$,
    disabled: false,
    submit,
    cancel,
    executorDialog,
    observerUsersDialog,
    controlUsersDialog,
  }
}
